import { View, Text, ScrollView, StyleSheet } from "react-native";
import React, { useMemo } from "react";
import Chip from "components/Chip";
import { EventAvailabilityDetails, EventType } from "features/types";
import dayjs from "dayjs";
import { useAppSelector } from "hooks/useAppSelector";
import { useAppDispatch } from "hooks/useAppDispatch";
import { setEventFilters } from "features/slices/reservationFlowSlice";
import { eventTypeDisplay } from "utils/eventUtils";

interface Props {
  toggled: boolean;
  schedule: EventAvailabilityDetails[];
  monthFormat: string;
  dayFormat: string;
}

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const sortDays = (dayA: string, dayB: string) => {
  const indexA = DAYS.indexOf(dayA);
  const indexB = DAYS.indexOf(dayB);
  return indexA - indexB;
};

const Filters: React.FC<Props> = (props) => {
  const { toggled, schedule, monthFormat, dayFormat } = props;

  const dispatch = useAppDispatch();
  const { eventFilters } = useAppSelector((state) => state.reservationFlow);
  const eventTypes = useMemo(() => {
    const eventTypes = new Set<EventType>();
    schedule.forEach((item) => eventTypes.add(item.eventType));
    return Array.from(eventTypes);
  }, [schedule]);

  const eventMonths = useMemo(() => {
    const months = new Set<string>();
    schedule.forEach((event) => {
      const monthOfEvent = dayjs(event.date)
        .tz(event.timezone)
        .format(monthFormat);
      months.add(monthOfEvent);
    });
    return Array.from(months);
  }, [schedule]);

  const eventDays = useMemo(() => {
    const days = new Set<string>();
    schedule.forEach((event) => {
      const dayOfEvent = dayjs(event.date).tz(event.timezone).format(dayFormat);
      days.add(dayOfEvent);
    });

    return Array.from(days).sort(sortDays);
  }, [schedule]);

  return (
    <View
      style={[
        styles.filterContainer,
        {
          height: toggled ? 120 : 0,
        },
      ]}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <Text>Event Types:</Text>
        <ScrollView
          horizontal
          contentContainerStyle={styles.scrollViewContentStyle}
        >
          <Chip
            selected={eventFilters.eventTypes.length === 0}
            onPress={() =>
              dispatch(setEventFilters({ ...eventFilters, eventTypes: [] }))
            }
          >
            All
          </Chip>
          <View style={{ marginRight: 5 }}></View>
          {eventTypes.length > 1 &&
            eventTypes.map((eventType) => (
              <View key={eventType} style={{ marginRight: 5 }}>
                <Chip
                  selected={eventFilters.eventTypes.includes(eventType)}
                  onPress={() =>
                    dispatch(
                      setEventFilters({
                        ...eventFilters,
                        eventTypes: [...eventFilters.eventTypes, eventType],
                      })
                    )
                  }
                >
                  {eventTypeDisplay(eventType)}
                </Chip>
              </View>
            ))}
        </ScrollView>
      </View>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <Text>Months:</Text>
        <ScrollView
          horizontal
          contentContainerStyle={styles.scrollViewContentStyle}
        >
          <Chip
            selected={eventFilters.months.length === 0}
            onPress={() =>
              dispatch(setEventFilters({ ...eventFilters, months: [] }))
            }
          >
            All
          </Chip>
          <View style={{ marginRight: 5 }}></View>
          {eventMonths.length > 1 &&
            eventMonths.map((month) => (
              <View key={month} style={{ marginRight: 5 }}>
                <Chip
                  selected={eventFilters.months.includes(month)}
                  onPress={() =>
                    dispatch(
                      setEventFilters({
                        ...eventFilters,
                        months: [...eventFilters.months, month],
                      })
                    )
                  }
                >
                  {month}
                </Chip>
              </View>
            ))}
        </ScrollView>
      </View>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <Text>Days:</Text>
        <ScrollView
          horizontal
          contentContainerStyle={styles.scrollViewContentStyle}
        >
          <Chip
            selected={eventFilters.days.length === 0}
            onPress={() => {
              dispatch(setEventFilters({ ...eventFilters, days: [] }));
            }}
          >
            All
          </Chip>
          <View style={{ marginRight: 5 }}></View>
          {eventDays.length > 1 &&
            eventDays.map((day) => (
              <View key={day} style={{ marginRight: 5 }}>
                <Chip
                  selected={eventFilters.days.includes(day)}
                  onPress={() =>
                    dispatch(
                      setEventFilters({
                        ...eventFilters,
                        days: [...eventFilters.days, day],
                      })
                    )
                  }
                >
                  {day}
                </Chip>
              </View>
            ))}
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  filterContainer: {
    backgroundColor: "#f8f9f9",
    overflow: "hidden",
    paddingHorizontal: 10,
    justifyContent: "center",
  },
  scrollViewContentStyle: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    padding: 4,
  },
});
export default Filters;
